import axios from "axios";
import { withRouter } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@material-ui/core";
import { ref, uploadString, getDownloadURL } from "@firebase/storage";
import { storage } from "../../main-component/Firebase";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import EventConfirmationFields from "../EventConfirmationFields";
import { EVENT_CONFIRMATION_DEFAULTS } from "../../utils/eventConfirmationCopy";
import "react-day-picker/style.css";
import "./calendar-styles.css";

const HOURS = [
  "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00", "21:00", "22:00"
];

/* ── Custom hour picker (4 items visible + scroll) ── */
const HourSelect = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="hour-select" ref={wrapRef}>
      <button
        type="button"
        className={`hour-select__trigger${open ? " hour-select__trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? "hour-select__value" : "hour-select__placeholder"}>
          {value || placeholder}
        </span>
        <svg
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          className={`hour-select__chevron${open ? " hour-select__chevron--open" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul className="hour-select__list" role="listbox">
          {HOURS.map((h) => (
            <li
              key={h}
              role="option"
              aria-selected={value === h}
              className={`hour-select__option${value === h ? " hour-select__option--selected" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(h); setOpen(false); }}
            >
              {h}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminCreateEventForm = (props) => {
  const { name, history, onSuccess } = props;
  const [author, setAuthor] = useState(name || "");
  const [title, setTitle] = useState("");
  const [pageName, setPageName] = useState("");
  const [location, setLocation] = useState("");
  const [text, setText] = useState("");
  const [base64URL, setBase64URL] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [imageName, setImageName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState({});
  const [eventType, setEventType] = useState("Fizic");
  const [confirmationPretitle, setConfirmationPretitle] = useState(
    EVENT_CONFIRMATION_DEFAULTS.pretitle
  );
  const [confirmationTitle, setConfirmationTitle] = useState(
    EVENT_CONFIRMATION_DEFAULTS.title
  );
  const [confirmationText, setConfirmationText] = useState(
    EVENT_CONFIRMATION_DEFAULTS.text
  );

  // Calendar state
  const [dateRange, setDateRange] = useState(undefined);
  const [startHour, setStartHour] = useState("");
  const [endHour, setEndHour] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  const editorRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setAuthor(name || "");
  }, [name]);

  const getBase64 = (file) => {
    return new Promise((resolve) => {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });
  };

  const uploadImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagePreview(file);
    setImageName(file.name);
    setError((prev) => ({ ...prev, imageName: "" }));
    getBase64(file).then((result) => {
      setBase64URL(result);
    });
  };

  const buildTimeInterval = () => {
    let interval = "";
    if (dateRange?.from) {
      interval += format(dateRange.from, "d MMMM yyyy", { locale: ro });
      if (dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime()) {
        interval += " - " + format(dateRange.to, "d MMMM yyyy", { locale: ro });
      }
    }
    if (startHour && endHour) {
      interval += `, ${startHour} - ${endHour}`;
    } else if (startHour) {
      interval += `, ${startHour}`;
    }
    return interval;
  };

  const createEvent = async () => {
    const imageRef = ref(storage, `campaign/${pageName}`);
    await uploadString(imageRef, base64URL, "data_url");
    const downloadURL = await getDownloadURL(imageRef);
    setCoverImage(downloadURL);
    await postRequest(downloadURL);
  };

  const postRequest = async (imageUrl) => {
    const timeInterval = buildTimeInterval();
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/campaign/post?id=${pageName}`,
        {
          id: pageName,
          title,
          author,
          createDate: Date.now(),
          appointments: 0,
          text,
          coverImage: imageUrl,
          location,
          timeInterval,
          dateFrom: dateRange?.from ? dateRange.from.toISOString() : "",
          dateTo: dateRange?.to ? dateRange.to.toISOString() : "",
          startHour,
          endHour,
          eventType,
          confirmationPretitle,
          confirmationTitle,
          confirmationText,
        },
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      toast.success("Eveniment creat cu succes!");
      resetForm();
      if (onSuccess) onSuccess();
      history.push(`/eveniment/${pageName}`);
    } catch (err) {
      console.error(err);
      toast.warning("Evenimentul NU a fost creat");
    }
  };

  const resetForm = () => {
    setAuthor(name || "");
    setTitle("");
    setPageName("");
    setLocation("");
    setText("");
    setBase64URL("");
    setCoverImage("");
    setImageName("");
    setImagePreview(null);
    setDateRange(undefined);
    setStartHour("");
    setEndHour("");
    setError({});
    setEventType("Fizic");
    setConfirmationPretitle(EVENT_CONFIRMATION_DEFAULTS.pretitle);
    setConfirmationTitle(EVENT_CONFIRMATION_DEFAULTS.title);
    setConfirmationText(EVENT_CONFIRMATION_DEFAULTS.text);
    if (inputRef.current) inputRef.current.value = null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newError = {};

    if (!title) newError.title = "Te rugăm să introduci titlul";
    if (!pageName) newError.pageName = "Te rugăm să introduci un nume pentru pagină";
    if (!imageName) newError.imageName = "Te rugăm să introduci imaginea de copertă";
    if (!dateRange?.from) newError.date = "Te rugăm să selectezi data evenimentului";

    setError(newError);

    if (Object.keys(newError).length === 0) {
      createEvent();
    }
  };

  const getDateLabel = () => {
    if (!dateRange?.from) return "";
    let label = format(dateRange.from, "d MMM yyyy", { locale: ro });
    if (dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime()) {
      label += " → " + format(dateRange.to, "d MMM yyyy", { locale: ro });
    }
    return label;
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="row">
        <div className="col-lg-12 col-md-12 col-12">
          <div className="form-field">
            <input
              onChange={(e) => { setTitle(e.target.value); setError((p) => ({ ...p, title: "" })); }}
              value={title}
              type="text"
              name="title"
              placeholder="Titlu"
            />
            <p>{error.title || ""}</p>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field">
            <input
              value={author}
              onChange={(e) => { setAuthor(e.target.value); setError((p) => ({ ...p, author: "" })); }}
              type="text"
              name="author"
              placeholder="Autor"
            />
            <p>{error.author || ""}</p>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field">
            <input
              value={pageName}
              onChange={(e) => { setPageName(e.target.value); setError((p) => ({ ...p, pageName: "" })); }}
              type="text"
              name="pageName"
              placeholder="the-inner-cluj-2026"
            />
            <p>{error.pageName || ""}</p>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontFamily: "Montserrat, sans-serif", fontSize: "12px", fontWeight: 600, color: "#555", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Tip Eveniment
            </label>
            <div style={{ display: "flex", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", width: "fit-content" }}>
              <button
                type="button"
                onClick={() => setEventType("Fizic")}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  background: eventType === "Fizic" ? "#904d71" : "#fff",
                  color: eventType === "Fizic" ? "#fff" : "#666",
                  transition: "all 0.2s ease",
                }}
              >
                Fizic
              </button>
              <button
                type="button"
                onClick={() => setEventType("Online")}
                style={{
                  padding: "8px 20px",
                  border: "none",
                  borderLeft: "1px solid #e0e0e0",
                  cursor: "pointer",
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  background: eventType === "Online" ? "#904d71" : "#fff",
                  color: eventType === "Online" ? "#fff" : "#666",
                  transition: "all 0.2s ease",
                }}
              >
                Online
              </button>
            </div>
          </div>
        </div>
        <div className="col-lg-12 col-md-12 col-12">
          <div className="form-field">
            <input
              value={location}
              onChange={(e) => { setLocation(e.target.value); setError((p) => ({ ...p, location: "" })); }}
              type="text"
              name="location"
              placeholder="Locație (ex: Cinema Florin Piersic, Cluj)"
            />
            <p>{error.location || ""}</p>
          </div>
        </div>

        {/* Visual Calendar + Time Picker */}
        <div className="col-lg-12 col-md-12 col-12">
          <div className="form-field">
            <div className="admin-calendar-wrapper">
              <button
                type="button"
                className="admin-calendar-trigger"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>{getDateLabel() || "Alege perioada"}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-calendar-chevron">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showCalendar && (
                <div className="admin-calendar-dropdown">
                  <DayPicker
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range);
                      setError((p) => ({ ...p, date: "" }));
                    }}
                    locale={ro}
                    numberOfMonths={1}
                    showOutsideDays
                    className="admin-rdp"
                  />
                  <button
                    type="button"
                    className="admin-calendar-done"
                    onClick={() => setShowCalendar(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Confirmă selecția
                  </button>
                </div>
              )}
              <p>{error.date || ""}</p>
            </div>
          </div>
        </div>

        {/* Time Dropdowns */}
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field">
            <label className="admin-calendar-label">Ora de început</label>
            <HourSelect
              value={startHour}
              onChange={(h) => setStartHour(h)}
              placeholder="-- Selectează --"
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field">
            <label className="admin-calendar-label">Ora de sfârșit</label>
            <HourSelect
              value={endHour}
              onChange={(h) => setEndHour(h)}
              placeholder="-- Selectează --"
            />
          </div>
        </div>

        {/* Summary */}
        {(dateRange?.from || startHour || endHour) && (
          <div className="col-lg-12">
            <div className="admin-datetime-summary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{buildTimeInterval() || "—"}</span>
            </div>
          </div>
        )}

        <div className="col-lg-12">
          <div className="form-field">
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              onInit={(evt, editor) => (editorRef.current = editor)}
              onEditorChange={(value) => { setText(value); setError((p) => ({ ...p, text: "" })); }}
              init={{
                height: 500,
                menubar: false,
                plugins: [
                  "advlist", "autolink", "lists", "link", "image", "charmap",
                  "preview", "anchor", "searchreplace", "visualblocks", "code",
                  "fullscreen", "insertdatetime", "media", "table", "code",
                  "help", "wordcount",
                ],
                toolbar:
                  "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
                content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              }}
            />
            <p>{error.text || ""}</p>
          </div>
        </div>
        <div className="col-lg-12">
          <EventConfirmationFields
            pretitle={confirmationPretitle}
            title={confirmationTitle}
            text={confirmationText}
            onPretitleChange={setConfirmationPretitle}
            onTitleChange={setConfirmationTitle}
            onTextChange={setConfirmationText}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-12">
          <div className="form-field">
            <Button
              variant="contained"
              onClick={() => inputRef.current.click()}
            >
              Încarcǎ fişier
            </Button>
            <input
              onChange={uploadImage}
              type="file"
              ref={inputRef}
              name="imageName"
              placeholder="Cover image"
              accept="image/png, image/gif, image/jpeg"
              hidden
            />
            <p>{error.imageName || ""}</p>
            {imagePreview && (
              <div style={{ width: 300, maxHeight: 300, borderRadius: 8, overflow: "hidden", marginTop: 12 }}>
                <img style={{ width: "100%" }} src={URL.createObjectURL(imagePreview)} alt="preview" />
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-12">
          <div className="form-submit">
            <button type="submit" className="theme-btn-s4 mt-4">
              Trimite
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default withRouter(AdminCreateEventForm);
